'use client';

import React, { useState } from 'react';
import { ExternalLink, ShieldCheck, Truck, AlertTriangle, CheckCircle, XCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { getListingsForProduct, getRetailerById, buildAffiliateUrl, type StockStatus } from '@/lib/mockData';

const STOCK_CONFIG: Record<StockStatus, { label: string; className: string; icon: React.ReactNode }> = {
  'in-stock': {
    label: 'En stock',
    className: 'bg-green-100 text-green-700',
    icon: <CheckCircle size={12} />,
  },
  'low-stock': {
    label: 'Stock limité',
    className: 'bg-amber-100 text-amber-700',
    icon: <AlertTriangle size={12} />,
  },
  'out-of-stock': {
    label: 'Rupture',
    className: 'bg-red-100 text-red-600',
    icon: <XCircle size={12} />,
  },
};

interface Props {
  productId: string;
  weightKg: number;
}

export default function PriceComparisonTable({ productId, weightKg }: Props) {
  const [sortField, setSortField] = useState<'price' | 'pricePerKg' | 'shipping'>('price');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const listings = getListingsForProduct(productId, weightKg);

  const sorted = [...listings].sort((a, b) => {
    let aVal = sortField === 'price' ? a.price : sortField === 'pricePerKg' ? a.pricePerKg : (a.shippingCost ?? 0);
    let bVal = sortField === 'price' ? b.price : sortField === 'pricePerKg' ? b.pricePerKg : (b.shippingCost ?? 0);
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  function toggleSort(field: typeof sortField) {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function SortIcon({ field }: { field: typeof sortField }) {
    if (sortField !== field) return <ChevronUp size={12} className="text-gray-300" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={12} className="text-orange-500" />
    ) : (
      <ChevronDown size={12} className="text-orange-500" />
    );
  }

  const bestPrice = sorted.filter((l) => l.stockStatus !== 'out-of-stock')[0]?.price;

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-orange-100 flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-gray-900 text-lg">
            Comparaison des prix
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {listings.length} marchand{listings.length !== 1 ? 's' : ''} · Format {weightKg} kg · Mis à jour le 26/04/2026
          </p>
        </div>
        <div className="bg-orange-50 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-orange-200">
          Meilleur prix : {bestPrice ? `${bestPrice.toFixed(2)} €` : '—'}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-orange-50/60 border-b border-orange-100">
              <th className="text-left px-5 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">
                Marchand
              </th>
              <th
                className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide cursor-pointer hover:text-orange-600 transition-colors select-none"
                onClick={() => toggleSort('price')}
              >
                <div className="flex items-center justify-end gap-1">
                  Prix total
                  <SortIcon field="price" />
                </div>
              </th>
              <th
                className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide cursor-pointer hover:text-orange-600 transition-colors select-none"
                onClick={() => toggleSort('pricePerKg')}
              >
                <div className="flex items-center justify-end gap-1">
                  Prix/kg
                  <SortIcon field="pricePerKg" />
                </div>
              </th>
              <th
                className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide cursor-pointer hover:text-orange-600 transition-colors select-none"
                onClick={() => toggleSort('shipping')}
              >
                <div className="flex items-center justify-end gap-1">
                  Livraison
                  <SortIcon field="shipping" />
                </div>
              </th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">
                Disponibilité
              </th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((listing, idx) => {
              const retailer = getRetailerById(listing.retailerId);
              const isBest = listing.price === bestPrice && listing.stockStatus !== 'out-of-stock';
              const stockCfg = STOCK_CONFIG[listing.stockStatus];
              const savings = bestPrice && listing.price > bestPrice
                ? (listing.price - bestPrice).toFixed(2)
                : null;

              return (
                <tr
                  key={`row-${listing.id}`}
                  className={`border-b border-gray-50 last:border-0 transition-colors ${
                    listing.stockStatus === 'out-of-stock' ?'opacity-50 bg-gray-50/50'
                      : isBest
                      ? 'bg-orange-50/40 hover:bg-orange-50/70' :'hover:bg-orange-50/30'
                  }`}
                >
                  {/* Retailer */}
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      {/* Rank */}
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          isBest ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {idx + 1}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span
                            className="font-semibold text-sm"
                            style={{ color: retailer?.logoColor ?? '#374151' }}
                          >
                            {retailer?.name ?? listing.retailerId}
                          </span>
                          {retailer?.trustBadge && (
                            <ShieldCheck size={13} className="text-teal-500" title="Marchand vérifié" />
                          )}
                          {isBest && (
                            <span className="bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                              Meilleur prix
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">
                          Mis à jour le {listing.lastUpdated.split('-').reverse().join('/')}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Price */}
                  <td className="px-4 py-4 text-right">
                    <div>
                      <span
                        className={`font-display font-bold tabular-nums text-base ${
                          isBest ? 'text-orange-600' : 'text-gray-800'
                        }`}
                      >
                        {listing.price.toFixed(2)} €
                      </span>
                      {savings && (
                        <p className="text-xs text-red-500 tabular-nums">+{savings} €</p>
                      )}
                    </div>
                  </td>

                  {/* Price per kg */}
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-semibold text-teal-700 tabular-nums">
                      {listing.pricePerKg.toFixed(2)} €/kg
                    </span>
                  </td>

                  {/* Shipping */}
                  <td className="px-4 py-4 text-right">
                    {listing.shippingCost === null ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        <Truck size={11} />
                        Gratuite
                      </span>
                    ) : (
                      <span className="text-sm text-gray-600 tabular-nums">
                        {listing.shippingCost.toFixed(2)} €
                      </span>
                    )}
                  </td>

                  {/* Stock */}
                  <td className="px-4 py-4">
                    <div className="flex justify-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${stockCfg.className}`}>
                        {stockCfg.icon}
                        {stockCfg.label}
                      </span>
                    </div>
                  </td>

                  {/* CTA */}
                  <td className="px-5 py-4">
                    {listing.stockStatus !== 'out-of-stock' ? (
                      <a
                        href={buildAffiliateUrl(listing.url, listing.retailerId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl transition-all duration-150 active:scale-95 whitespace-nowrap ${
                          isBest
                            ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-sm hover:shadow-md'
                            : 'bg-white border border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-400'
                        }`}
                      >
                        Acheter
                        <ExternalLink size={13} />
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Indisponible</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer note */}
      <div className="px-5 py-3 bg-gray-50/50 border-t border-orange-50">
        <p className="text-xs text-gray-400">
          Les prix sont indicatifs et peuvent varier. Vérifiez toujours le prix définitif sur le site du marchand avant l'achat. CroquetteCompare peut percevoir une commission sur les achats effectués via nos liens.
        </p>
      </div>
    </div>
  );
}